"use client";

import * as React from "react";
import clsx from "clsx";

const locationHebrewName = {
  "AHAD_HA'AM": "אחד העם",
  SAN_MARTIN: "סן מרטין",
  ZVI: "שדרות צבי",
  BERNSTEIN: "פרץ ברנשטיין",
  CENTER_OF_THE_PARK: "לב הפארק",
  SHAKH: "שחר",
  CHEN: "חן",
};

function vacancyReady(raw) {
  return raw != null && raw !== "" && (raw === "אלמ" || !Number.isNaN(Number(raw)));
}

function normalizeVacancy(raw) {
  if (raw === "אלמ" || Number.isNaN(Number(raw))) return 0;
  return Math.max(0, Number(raw));
}

/** Fill color by how full the lot is — green / yellow / red. */
function fillClass(occupancyPct) {
  if (occupancyPct >= 90) return "bg-red-500";
  if (occupancyPct >= 43) return "bg-yellow-400";
  return "bg-green-500";
}

function LotMeter({ name, totalSpaces, vacancyRaw }) {
  const total = Math.max(0, Number(totalSpaces) || 0);
  const loaded = vacancyReady(vacancyRaw);
  const vacancy = loaded ? normalizeVacancy(vacancyRaw) : null;
  const occupied = loaded ? Math.max(0, total - vacancy) : null;
  const occupancyPct =
    loaded && total > 0 ? Math.min(100, Math.round((occupied / total) * 100)) : null;
  const title = locationHebrewName[name] || name;

  return (
    <div
      className="flex-1 min-w-[100px] max-w-[180px] rounded-lg bg-white/80 border border-gray-200/80 px-2.5 py-2 shadow-sm"
      title={
        loaded
          ? `${title}: תפוס ${occupied}, פנוי ${vacancy} מתוך ${total}`
          : `${title}: טוען תפוסה`
      }
    >
      <div className="flex items-baseline justify-between gap-1 mb-1.5" dir="rtl">
        <span className="text-[11px] font-semibold text-gray-800 truncate">{title}</span>
        <span
          className={clsx(
            "text-[11px] font-bold tabular-nums shrink-0",
            occupancyPct == null
              ? "text-gray-400"
              : occupancyPct >= 90
                ? "text-red-600"
                : occupancyPct >= 43
                  ? "text-yellow-500"
                  : "text-gray-700"
          )}
        >
          {occupancyPct == null ? "—" : `${occupancyPct}%`}
        </span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden" dir="ltr">
        {occupancyPct == null ? (
          <div className="h-full w-full bg-gray-200 animate-pulse rounded-full" />
        ) : (
          <div
            className={clsx("h-full rounded-full", fillClass(occupancyPct))}
            style={{ width: `${occupancyPct}%` }}
          />
        )}
      </div>
      <div className="mt-1 text-[10px] text-gray-500 tabular-nums text-left" dir="ltr">
        {loaded ? `${occupied}/${total}` : `—/${total}`}
      </div>
    </div>
  );
}

/**
 * Full-width live occupancy strip — one meter per lot, no dropdown / gauges.
 */
export function NationalParkOccupancyStrip({
  vacancyValues = [],
  totalParkingSpaces = [],
  deviceNames = [],
}) {
  if (!deviceNames.length) return null;

  return (
    <div className="w-full mb-4 rounded-xl border border-gray-200 bg-gradient-to-l from-slate-50 to-white px-3 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5" dir="rtl">
        <div className="text-right">
          <div className="text-sm font-bold text-gray-900">תפוסה חיה</div>
          <div className="text-[11px] text-gray-500">מקומות תפוסים מול קיבולת בכל חניון</div>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-gray-500">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500" /> פנוי יחסית
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-yellow-400" /> עמוס
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500" /> כמעט מלא
          </span>
        </div>
      </div>
      <div className="flex flex-wrap justify-center sm:justify-between gap-2">
        {deviceNames.map((name, i) => (
          <LotMeter
            key={name}
            name={name}
            totalSpaces={totalParkingSpaces[i]}
            vacancyRaw={vacancyValues[i]}
          />
        ))}
      </div>
    </div>
  );
}

export default NationalParkOccupancyStrip;
